import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/activities',   label: 'Activities' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/about-hub',    label: 'About' },
  { to: '/mindful-play', label: 'Mindful Play' },
  { to: '/reach-us',     label: 'Contact' },
];

export default function Nav() {
  const [open, setOpen]       = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location              = useLocation();

  useEffect(() => { setOpen(false); }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 90,
      background: scrolled
        ? 'rgba(13,31,53,0.96)'
        : 'rgba(13,31,53,0.7)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(168,207,224,0.08)',
      transition: 'background 0.3s',
    }}>
      <div style={{
        maxWidth: '1120px', margin: '0 auto',
        padding: '0 24px',
        height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>

        {/* Logo */}
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          textDecoration: 'none',
        }}>
          {/* Compass SVG logo */}
          <svg width="34" height="34" viewBox="0 0 34 34" fill="none" aria-hidden="true">
            <circle cx="17" cy="17" r="16" stroke="rgba(201,168,76,0.5)" strokeWidth="1.2" />
            <circle cx="17" cy="17" r="11" stroke="rgba(168,207,224,0.2)" strokeWidth="0.8" />
            <path d="M17 6 L19 17 L17 28 L15 17 Z" fill="#c9a84c" />
            <path d="M6 17 L17 15 L28 17 L17 19 Z" fill="rgba(168,207,224,0.5)" />
            <circle cx="17" cy="17" r="2" fill="#c9a84c" />
            <text x="17" y="3.5" textAnchor="middle" fontSize="3.5" fill="rgba(201,168,76,0.8)" fontFamily="Syne, sans-serif" fontWeight="700">N</text>
            <text x="17" y="32.5" textAnchor="middle" fontSize="3.5" fill="rgba(168,207,224,0.5)" fontFamily="Syne, sans-serif">S</text>
            <text x="31" y="17.8" textAnchor="middle" fontSize="3.5" fill="rgba(168,207,224,0.5)" fontFamily="Syne, sans-serif">E</text>
            <text x="3" y="17.8" textAnchor="middle" fontSize="3.5" fill="rgba(168,207,224,0.5)" fontFamily="Syne, sans-serif">W</text>
          </svg>
          <div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#fff', lineHeight: 1 }}>Social North Hub</div>
            <div style={{ fontSize: '0.6rem', color: 'rgba(201,168,76,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '2px' }}>Canada · 18+</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav aria-label="Main navigation" style={{ display: 'flex', gap: '4px', alignItems: 'center' }} className="hide-mobile">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} style={({ isActive }) => ({
              fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: '0.85rem',
              padding: '7px 14px', borderRadius: '999px',
              color: isActive ? '#c9a84c' : 'rgba(168,207,224,0.7)',
              background: isActive ? 'rgba(201,168,76,0.1)' : 'transparent',
              transition: 'all 0.2s', textDecoration: 'none',
              letterSpacing: '0.01em',
            })}
            onMouseEnter={e => { if (!e.target.classList.contains('active')) e.target.style.color = '#fff'; }}
            onMouseLeave={e => { if (!e.target.classList.contains('active')) e.target.style.color = 'rgba(168,207,224,0.7)'; }}
            >
              {label}
            </NavLink>
          ))}
          <Link to="/activities" className="btn btn-primary" style={{ marginLeft: '8px', padding: '9px 22px', fontSize: '0.85rem' }}>
            Open Activities
          </Link>
        </nav>

        {/* Mobile burger */}
        <button
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          className="hide-desktop"
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', gap: '5px', padding: '4px',
          }}
        >
          {[0,1,2].map(i => (
            <span key={i} style={{
              display: 'block', width: '24px', height: '2px',
              background: 'rgba(168,207,224,0.8)', borderRadius: '1px',
              transition: 'all 0.3s',
              transform: open
                ? i === 0 ? 'rotate(45deg) translate(5px, 5px)'
                  : i === 2 ? 'rotate(-45deg) translate(5px, -5px)'
                  : 'scaleX(0)'
                : 'none',
              opacity: open && i === 1 ? 0 : 1,
            }} />
          ))}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <nav aria-label="Mobile navigation" style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'rgba(13,31,53,0.98)',
          borderBottom: '1px solid rgba(168,207,224,0.1)',
          padding: '16px 24px 24px',
        }}>
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink key={to} to={to} style={({ isActive }) => ({
              display: 'block', padding: '13px 0',
              borderBottom: '1px solid rgba(168,207,224,0.07)',
              fontFamily: 'Syne, sans-serif', fontWeight: 600,
              fontSize: '1.1rem', textDecoration: 'none',
              color: isActive ? '#c9a84c' : 'rgba(168,207,224,0.8)',
            })}>
              {label}
            </NavLink>
          ))}
          <Link to="/activities" className="btn btn-primary" style={{ marginTop: '16px', width: '100%', justifyContent: 'center' }}>
            Open Activities
          </Link>
        </nav>
      )}
    </header>
  );
}
